import { TableState } from '@react-stately/table';
import { TableCollection } from '@react-types/table';
import React, { useMemo } from 'react';

import { Spinner } from '../index';

import { GridNode } from '@react-types/grid';
import { cx } from 'class-variance-authority';
import TableCell from './Cell';
import TableCheckboxCell from './CheckboxCell';
import { useTableContext } from './Context';
import useIsInfinityScroll from './hooks/useIsInfinityScroll';
import TableRow from './Row';
import TableRowGroup from './RowGroup';
import { loadingRowClassNames } from './styles';

interface Props<T> {
    state: TableState<T>;
    collection: TableCollection<T>;
    animated?: boolean;
    isHoverable?: boolean;
    hideLoading?: boolean;
    hasPagination?: boolean;
    // @internal
    isStatic?: boolean;
    color?: string;
    children?: React.ReactNode;
}

type CellProps<T> = GridNode<T> & { rendered: React.ReactNode };

type NativeAttrs = Omit<React.HTMLAttributes<unknown>, keyof Props<any>>;

export type TableBodyProps<T = unknown> = Props<T> & NativeAttrs;

// TODO: Remove this once we have a better way to pass it from the parent
const SCROLL_OFFSET = 40;

const TableBody: React.FC<React.PropsWithChildren<React.PropsWithChildren<TableBodyProps>>> = ({
    children,
    collection,
    state,
    animated,
    color,
    hasPagination,
    isHoverable,
    hideLoading,
    isStatic,
    ...props
}) => {
    const { currentPage, rowsPerPage, collection: collectionContext, setCollection, rowClassName } = useTableContext();

    const infinityScroll = useIsInfinityScroll(collection);

    // Ref to the scrollable tbody, used to detect when its content does not overflow.
    const scrollRef = React.useRef<HTMLTableSectionElement>(null);

    React.useEffect(() => {
        if (collection !== collectionContext) {
            setCollection?.(collection);
        }
    }, [collection, collectionContext]);

    const loadingState = collection.body?.props?.loadingState;
    const isLoading = loadingState === 'loading' || loadingState === 'loadingMore';

    // Request the next page whenever a bounded scroll container has meaningful empty
    // space below its content. Without a scrollbar the scroll event never fires, so short
    // pages would otherwise get stuck on page 1; loadMore is a no-op once data runs out.
    //
    // We compare the true (unclamped) content height — the sum of the row heights — against
    // the container height, NOT element.scrollHeight: scrollHeight is clamped up to
    // clientHeight, so it can't distinguish "bounded container, not yet full" (should load)
    // from "height:auto container sized to its content" (must NOT load, or it loads forever).
    const maybeLoadMore = React.useCallback(() => {
        if (!infinityScroll || isLoading || loadingState === 'error') {
            return;
        }
        const element = scrollRef.current;
        if (!element) {
            return;
        }
        const contentHeight = Array.from(element.children).reduce(
            (sum, child) => sum + (child as HTMLElement).offsetHeight,
            0
        );
        // A content-sized (unbounded) container has ~no gap, so this never fires there.
        // A bounded-but-underfilled container has a gap of at least one page of rows.
        const emptySpace = element.clientHeight - contentHeight;
        if (emptySpace >= SCROLL_OFFSET) {
            collection.body?.props?.onLoadMore?.();
        }
    }, [infinityScroll, isLoading, loadingState, collection]);

    // Re-check after each render (covers newly loaded rows growing the content).
    React.useEffect(() => {
        maybeLoadMore();
    }, [maybeLoadMore]);

    // Re-check when the container itself resizes (window resize, layout shift, zoom).
    React.useEffect(() => {
        const element = scrollRef.current;
        if (!element || typeof ResizeObserver === 'undefined') {
            return;
        }
        const observer = new ResizeObserver(() => maybeLoadMore());
        observer.observe(element);
        return () => observer.disconnect();
    }, [maybeLoadMore]);

    const rows = useMemo(() => {
        const data = Array.from(collection.body?.childNodes || []) as GridNode<unknown>[];

        if (!hasPagination) {
            return data;
        }

        return data?.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
    }, [hasPagination, collection, currentPage, rowsPerPage]);

    const completeSpaces = useMemo(() => {
        const rowsCount = rows.length;

        if (rowsCount >= rowsPerPage && !infinityScroll) {
            return null;
        }

        return (
            <tr aria-hidden="true" role="row">
                <td
                    aria-hidden="true"
                    colSpan={collection.columnCount}
                    className={cx(infinityScroll ? 'tw-h-11' : `tw-h-[calc(${rowsPerPage - rowsCount}*2.75rem]`)}
                    style={props?.style}
                />
            </tr>
        );
    }, [collection.columnCount, rows, rowsPerPage, infinityScroll]);

    // handle scroll and call next page on infinity scroll
    const handleScroll = (e: React.BaseSyntheticEvent) => {
        if (!infinityScroll) {
            return;
        }
        const element = e.target;
        const isAtBottom = element.scrollHeight - (element.scrollTop + SCROLL_OFFSET) <= element.clientHeight;

        if (isAtBottom && !isLoading) {
            collection.body?.props?.onLoadMore?.();
        }
    };

    return (
        <TableRowGroup as="tbody" ref={scrollRef} isInfinityScroll={infinityScroll} onScroll={handleScroll} {...props}>
            {children}
            {rows?.map((row) => {
                if (!row.hasChildNodes) {
                    return null;
                }

                return (
                    <TableRow
                        key={row?.key}
                        aria-hidden="false"
                        item={row}
                        state={state}
                        hoverable={isHoverable}
                        isInfinityScroll={infinityScroll}
                        className={rowClassName?.(row.value)}
                    >
                        {Array.from(row.childNodes).map((cell: CellProps<unknown>) =>
                            cell?.props?.isSelectionCell ? (
                                <TableCheckboxCell key={cell?.key} cell={cell} state={state} />
                            ) : (
                                <TableCell key={cell?.key} cell={cell} isStatic={isStatic} state={state} />
                            )
                        )}
                    </TableRow>
                );
            })}
            {!hideLoading && isLoading && (
                <tr
                    aria-hidden="true"
                    className={loadingRowClassNames({ isAbsolute: !infinityScroll, isAtEnd: rows.length > 0 })}
                    role="row"
                >
                    <td aria-hidden="true" colSpan={collection.columnCount}>
                        <Spinner color="success" />
                    </td>
                </tr>
            )}
            {completeSpaces}
        </TableRowGroup>
    );
};

TableBody.displayName = 'TableBody';

export default TableBody;
